/**
 * Dropbox Service
 * Frontend service for Dropbox integration
 * 
 * Fixes Bug #13: Add caching for status checks
 */

import axios from 'axios';

interface DropboxStatus {
  connected: boolean;
  status?: string;
  metadata?: {
    accountId: string;
    email: string;
    displayName: string;
  };
  expiresAt?: string;
}

interface SearchResult {
  success: boolean;
  files: any[];
  count: number;
  hasMore?: boolean;
  cursor?: string;
}

interface ReadResult {
  success: boolean;
  file: any;
}

class DropboxService {
  private baseUrl = '/api/dropbox';
  private statusCache: DropboxStatus | null = null;
  private statusCacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get integration status with caching
   * Fixes Bug #13: Cache status to reduce API calls
   */
  async getStatus(forceRefresh: boolean = false): Promise<DropboxStatus> {
    const now = Date.now();
    
    // Use cache if valid and not forcing refresh
    if (!forceRefresh && 
        this.statusCache && 
        now - this.statusCacheTimestamp < this.CACHE_DURATION) {
      return this.statusCache;
    }
    
    const response = await axios.get(`${this.baseUrl}/status`);
    this.statusCache = response.data;
    this.statusCacheTimestamp = now;
    
    return response.data;
  }

  /**
   * Clear status cache (call after connect/disconnect)
   */
  clearStatusCache(): void {
    this.statusCache = null;
    this.statusCacheTimestamp = 0;
  }

  async getAuthUrl(): Promise<{ authUrl: string; state: string }> {
    const response = await axios.get(`${this.baseUrl}/auth/url`);
    return response.data;
  }

  async handleCallback(code: string, state: string): Promise<any> {
    const response = await axios.post(`${this.baseUrl}/auth/callback`, null, {
      params: { code, state }
    });
    
    // Clear cache after successful connection
    this.clearStatusCache();
    
    return response.data;
  }

  async disconnect(): Promise<void> {
    await axios.delete(`${this.baseUrl}/disconnect`);
    
    // Clear cache after disconnect
    this.clearStatusCache();
  }

  async searchFiles(params: {
    query: string;
    fileTypes?: string[];
    maxResults?: number;
    cursor?: string;
  }): Promise<SearchResult> {
    const response = await axios.post(`${this.baseUrl}/search`, params);
    return response.data;
  }

  async readFile(params: {
    filePath: string;
    maxSizeMB?: number;
  }): Promise<ReadResult> {
    const response = await axios.post(`${this.baseUrl}/read`, params);
    return response.data;
  }
}

export const dropboxService = new DropboxService();
